/**
 * All static configuration for the Warranty Registration form lives here.
 * Keeping it out of the components means new fields / options / plans can
 * be added without touching any JSX or logic.
 */

import indiaData from "./indiaStatesDistricts.json";

// export const STORE_OPTIONS = ["Store 1", "Store 2", "Store 3"];
export const STORE_OPTIONS = [
  "NeverOwned - New Delhi",
  "NeverOwned - Bareilly",
  "NeverOwned - Aizawl",
  "NeverOwned - Indore",
  "NeverOwned - Ayodhya",
  "NeverOwned - Champhai",
  "NeverOwned - Kolasib",
  "NeverOwned - Lunglei",
  "NeverOwned - Saiha",
  "NeverOwned - Gurugram",
];

/** Full state list derived from the JSON — sorted alphabetically */
export const STATE_OPTIONS = indiaData.india_states_cities
  .map((s) => s.state)
  .sort((a, b) => a.localeCompare(b));

export const getDistrictsByState = (stateName) => {
  if (!stateName) return [];
  const entry = indiaData.india_states_cities.find(
    (s) => s.state === stateName,
  );
  return entry ? [...entry.districts].sort((a, b) => a.localeCompare(b)) : [];
};

// product category
export const PRODUCT_CATEGORY_OPTIONS = [
  "Air Conditioner",
  "Air Cooler",
  "Air Fryer",
  "Chimney",
  "Coffee Maker",
  "Deep Freezer",
  "Dishwasher",
  "Dryer",
  "Food Processor/Food Steamer",
  "Fans",
  "Garment Steamer",
  "Inverter",
  "Inverter Battery",
  "Juicer Mixer Grinder/Mixer Grinder/Mixer",
  "Laptops",
  "Microwave",
  "Mobile",
  "Massager",
  "Monitors",
  "Oil Heater/Electric Room Heater",
  "OTG",
  "Printers",
  "Refrigerator",
  "Speaker/Home Theatre",
  "Television",
  "Vacuum Cleaner",
  "Washing Machine",
  "Water Cooler",
  "Water Dispenser",
  "Water Geyser",
  "Water Purifier",
  "Wet Grinders",
];

/**
 * Mapping from plan category keyword → allowed Product Category options.
 * Used to filter the Product Category dropdown based on the selected plan card.
 * Keys are lowercase to allow case-insensitive matching.
 */
export const PLAN_CATEGORY_PRODUCT_MAP = {
  tv: ["Television"],
  appliances: [
    "Air Conditioner",
    "Air Cooler",
    "Air Fryer",
    // "AC", // added from image
    "Blender",
    "Chimney",
    "Coffee Maker",
    "Deep Freezer",
    "Dishwasher",
    "Dish Washer", // added from image
    "Dryer",
    "Food Processor/Food Steamer",
    "Fans",
    "Garment Steamer",
    "Gas Stoves",
    "Grooming Products",
    "Hair Straightener",
    "Induction",
    "Inverter",
    "Inverter Battery",
    "Juicer Mixer Grinder/Mixer Grinder/Mixer",
    "Juicer Mixer Grinder", // added from image
    "Laptop",
    "Microwave",
    "Massager",
    "Mobiles",
    "Oil Heater/Electric Room Heater",
    "OTG",
    "Refrigerator",
    "Smart Watches",
    "Speaker",
    "Stabilizer",
    "Vacuum Cleaner",
    "Washing Machine",
    "Water Cooler",
    "Water Dispenser",
    "Water Geyser",
    "Water Purifier",
    "Wet Grinders",
  ],
};
// export const PLAN_CATEGORY_PRODUCT_MAP = {
//   tv: ["Television"],
//   appliances: [
//     "Air Conditioner",
//     "Air Cooler",
//     "Air Fryer",
//     "Blender", // new
//     "Chimney",
//     "Coffee Maker",
//     "Deep Freezer",
//     "Dishwasher",
//     "Dryer",
//     "Food Processor/Food Steamer",
//     "Fans",
//     "Garment Steamer",
//     "Gas Stoves", // new
//     "Grooming Products", // new
//     "Hair Straightener", // new
//     "Induction", // new
//     "Inverter",
//     "Inverter Battery",
//     "Juicer Mixer Grinder/Mixer Grinder/Mixer",
//     "Laptop", // new
//     "Microwave",
//     "Massager",
//     "Mobiles", // new
//     "Oil Heater/Electric Room Heater",
//     "OTG",
//     "Refrigerator",
//     "Smart Watches", // new
//     "Speaker", // new
//     "Stabilizer", // new
//     "Vacuum Cleaner",
//     "Washing Machine",
//     "Water Cooler",
//     "Water Dispenser",
//     "Water Geyser",
//     "Water Purifier",
//     "Wet Grinders",
//   ],
// };

/**
 * Returns filtered product category options based on the plan's category.
 * Falls back to the full list if no match is found.
 */
export const getProductCategoryOptions = (planCategory = "") => {
  const key = planCategory.toLowerCase().trim();
  return PLAN_CATEGORY_PRODUCT_MAP[key] || PRODUCT_CATEGORY_OPTIONS;
};

export const BRAND_OPTIONS = [
  "Acer",
  "Adsun",
  "Agaro", // added
  "Aironova",
  "Akai",
  "Amazon",
  "Amazon Basics",
  "Amazfit", // added
  "Amstrad",
  "Apple", // added
  "ASUS", // added
  "Bajaj",
  "Bajaj Majesty",
  "Beyond Appliances", // added
  "Black + Decker",
  "Blaupunkt",
  "Blue Star",
  "Boat",
  "boAt", // added (image casing)
  "Borosil Prima",
  "Bosch",
  "Boss",
  "Bpl",
  "Braun", // added
  "Candy",
  "Carrier",
  "Celecor",
  "Clarion",
  "Coway",
  "Cromaa",
  "Cruise", // added
  "Cuckoo",
  "Daikin",
  "Daiwa",
  "Deciwood",
  "Dell",
  "Dmr",
  "Dreame",
  "Dyson",
  "Eairtec",
  "Ecovacs",
  "Electrolux",
  "Elica",
  "Eurodomo",
  "Everest", // added
  "F&D",
  "Faber",
  "Fastrack", // added
  "Felixe",
  "Fire-Boltt", // added
  "Flipkart SmartBuy",
  "Galanz",
  "Glen",
  "Godrej",
  "Haier",
  "Harman Kardon",
  "Havells", // added
  "Hindware",
  "Hisense",
  "Hitachi",
  "Honeywell",
  "Hp",
  "HP", // added (image casing)
  "Hyundai",
  "Iball",
  "Ifb",
  "IFB", // added (image casing)
  "Iffalcon",
  "Ikonic", // added
  "Impex",
  "Inalsa",
  "Infinity",
  "Infinix Led",
  "Intex",
  "I-Tel",
  "Jbl",
  "JBL", // added (image casing)
  "Kaff",
  "Kavin",
  "Kelvinator",
  "Kenstar",
  "Kent", // added
  "Kodak",
  "Koryo",
  "Krisons",
  "Kutchina",
  "Leeco",
  "Lenovo", // added
  "Lg",
  "LG", // added (image casing)
  "Livpure",
  "Lloyd",
  "Luminous",
  "Markson",
  "Marq",
  "Marshall",
  "Mi",
  "Microtek", // added
  "Midea",
  "Mitashi",
  "Mitsubishi",
  "Mivi", // added
  "Monitor", // added
  "Moonbow",
  "Morphy Ricahrds",
  "Motorola",
  "MSI", // added
  "Noise", // added
  "NoiseFit", // added
  "Noble Skioda",
  "Nova", // added
  "NVY", // added
  "O General",
  "Oneplus",
  "OnePlus", // added (image casing)
  "Onida",
  "Oppo", // added
  "Oscar",
  "Panasonic",
  "Philips",
  "Pigeon", // added
  "Pioneer",
  "Prestige",
  "Realme",
  "Ronix",
  "Samsung",
  "Sansui",
  "Sanyo",
  "Seavy",
  "Servo", // added
  "Sharp",
  "Shinco",
  "Siemens",
  "Signora Care",
  "Skywall",
  "Skyworth",
  "Sony",
  "Sunflame",
  "Syska", // added
  "Tcl",
  "Telefunken",
  "Thomson",
  "Tlc",
  "Toshiba", // added
  "Tvs Scs",
  "Usha",
  "Vega", // added
  "Videocon",
  "Vivo", // added
  "Voltas",
  "Vu",
  "Vw",
  "V-Guard", // added
  "Wahl", // added
  "Whirlpool",
  "White Westinghouse", // added
  "Wonderchef", // added
  "Wybor", // added
  "Xiaomi",
  "Xonier",
  "Yamaha",
  "Yc",
  "Zebronics",
];
// export const BRAND_OPTIONS = [
//   "Acer",
//   "Adsun",
//   "Aironova",
//   "Akai",
//   "Amazon",
//   "Amazon Basics",
//   "Amstrad",
//   "Bajaj",
//   "Bajaj Majesty",
//   "Black + Decker",
//   "Blaupunkt",
//   "Blue Star",
//   "Boat",
//   "Borosil Prima",
//   "Bosch",
//   "Boss",
//   "Bpl",
//   "Candy",
//   "Carrier",
//   "Celecor",
//   "Clarion",
//   "Coway",
//   "Cromaa",
//   "Cuckoo",
//   "Daikin",
//   "Daiwa",
//   "Deciwood",
//   "Dell",
//   "Dmr",
//   "Dreame",
//   "Dyson",
//   "Eairtec",
//   "Ecovacs",
//   "Electrolux",
//   "Elica",
//   "Eurodomo",
//   "F&D",
//   "Faber",
//   "Felixe",
//   "Flipkart SmartBuy",
//   "Galanz",
//   "Glen",
//   "Godrej",
//   "Haier",
//   "Harman Kardon",
//   "Hindware",
//   "Hisense",
//   "Hitachi",
//   "Honeywell",
//   "Hp",
//   "Hyundai",
//   "Iball",
//   "Ifb",
//   "Iffalcon",
//   "Impex",
//   "Inalsa",
//   "Infinity",
//   "Infinix Led",
//   "Intex",
//   "I-Tel",
//   "Jbl",
//   "Kaff",
//   "Kavin",
//   "Kelvinator",
//   "Kenstar",
//   "Kodak",
//   "Koryo",
//   "Krisons",
//   "Kutchina",
//   "Leeco",
//   "Lg",
//   "Livpure",
//   "Lloyd",
//   "Luminous",
//   "Markson",
//   "Marq",
//   "Marshall",
//   "Mi",
//   "Midea",
//   "Mitashi",
//   "Mitsubishi",
//   "Moonbow",
//   "Morphy Ricahrds",
//   "Motorola",
//   "Noble Skioda",
//   "O General",
//   "Oneplus",
//   "Onida",
//   "Oscar",
//   "Panasonic",
//   "Philips",
//   "Pioneer",
//   "Prestige",
//   "Realme",
//   "Ronix",
//   "Samsung",
//   "Sansui",
//   "Sanyo",
//   "Seavy",
//   "Sharp",
//   "Shinco",
//   "Siemens",
//   "Signora Care",
//   "Skywall",
//   "Skyworth",
//   "Sony",
//   "Sunflame",
//   "Tcl",
//   "Telefunken",
//   "Thomson",
//   "Tlc",
//   "Tvs Scs",
//   "Usha",
//   "Videocon",
//   "Voltas",
//   "Vu",
//   "Vw",
//   "Whirlpool",
//   "Xiaomi",
//   "Xonier",
//   "Yamaha",
//   "Yc",
//   "Zebronics",
// ];
export const TV_BRAND_OPTIONS = [
  "Samsung",
  "LG",
  "Xiaomi / Redmi",
  "TCL",
  "Sony",
  "Haier",
  "OnePlus",
  "Realme",
  "Hisense",
  "Acer",
  "Kodak",
  "Vu",
  "Thomson",
  "Toshiba",
  "Blaupunkt",
  "Sansui",
  "BPL",
  "Weston",
  "Bush",
  "DOR",
  "Flipkart SmartBuy",
  "NVY", // added
  "Mi",
  "Wobble",
];

export const EXTENDED_YEAR_OPTIONS = ["1 Year", "2 Year", "3 Year"];

export const EXISTING_BRAND_WARRANTY_OPTIONS = ["1 Year", "2 Year", "3 Year"];

/**
 * Customer Information section — rendered dynamically via `.map`.
 * `span` controls the grid width ("half" = paired 2-up sub-grid).
 */
export const CUSTOMER_FORM_FIELDS = [
  {
    id: "customerName",
    label: "Customer Name",
    type: "text",
    placeholder: "Full name",
    required: true,
  },
  {
    id: "mobileNumber",
    label: "Mobile Number",
    type: "text",
    inputMode: "numeric",
    maxLength: 10,
    placeholder: "10-digit number",
    required: true,
    sanitize: "digits",
  },
  {
    id: "email",
    label: "Email Address",
    type: "email",
    placeholder: "name@gamil.com",
    required: true,
  },
];

/** Rendered as a nested 2-up grid (State / Pin Code) */
export const CUSTOMER_FORM_FIELDS_PAIRED = [
  {
    id: "pinCode",
    label: "Pin Code",
    type: "text",
    inputMode: "numeric",
    maxLength: 6,
    placeholder: "12346",
    required: true,
    sanitize: "digits",
  },
];

/** Product Details section */
export const PRODUCT_FORM_FIELDS = [
  {
    id: "serialNumber",
    label: "Serial Number",
    type: "text",
    placeholder: "SN-XXXX-XXXX-XXXX",
    maxLength: 17,
    required: true,
    mono: true,
  },
  {
    id: "invoiceValue",
    label: "Product Invoice Value (Inclusive of GST)",
    type: "text",
    inputMode: "decimal",
    placeholder: "e.g. 45000",
    required: true,
  },
];

/**
 * Protection plan cards.
 * `iconSrc` should point at /public assets (bw.png / ew.png / amp.png).
 */
export const WARRANTY_PLANS = [
  {
    type: "brand",
    title: "Brand Warranty",
    description: "Standard  coverage included with your purchase.",
    badge: "1 Year Coverage",
    iconSrc: "/bw.png",
    hasDurationChips: false,
  },
  {
    type: "extended",
    title: "Extended Warranty",
    description: "Enhanced peace of mind with additional years of protection.",
    iconSrc: "/ew.png",
    hasDurationChips: true,
  },
  {
    type: "amp",
    title: "Annual Maintenance Plan",
    description: "Now available for air conditioners.",
    badge: "1 Year Coverage",
    iconSrc: "/amp.png",
    hasDurationChips: false,
  },
];

/** Values that seed the form's state on first render */
export const INITIAL_FORM_VALUES = {
  storeName: STORE_OPTIONS[0],
  customerName: "",
  mobileNumber: "",
  email: "",
  city: "",
  state: "",
  pinCode: "",
  productCategory: "",
  brandName: "",
  serialNumber: "",
  invoiceValue: "",
  extendedYears: EXTENDED_YEAR_OPTIONS[0],
  existingBrandWarranty: EXISTING_BRAND_WARRANTY_OPTIONS[0],
};
